import React, { Component } from 'react';
import { Form } from 'redux-form';
import { browserHistory } from 'react-router';
import idx from 'idx';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import PAPQuestionBase from 'pap/components/common/pap_question_base';
import PAPBase from 'pap/containers/common/pap_base';
import { getFieldPropsFromDefinition } from 'pap/utils';
import { getSubCategory, getCategory } from 'common/ducks/categories';
import { getStoredDiscountCode, getPendingDiscountCode } from 'pap/ducks/pricing';
import { getBannerContents } from 'common/components/discount_code';
import { getPapUrl } from 'pap_v2/utils';
import {
  trackGoogle, trackMixpanel, aliasWithMixpanel, identifyWithMixpanel, setMixpanelProfile,
  mixpanelProfileFromCsProfile, trackPerfectAudience, trackFacebookCustomEvent,
  trackClickWithMixpanel, MP_CHANGED_PAP_SECTION_EVENT_NAME
} from 'common/tracking';

/**
 * A component responsible for rendering all brief questions in one screen.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
export default class BriefAll extends Component {
  constructor(props) {
    super(props);

    this.state = {
      autoSaving: false,
      manualSaving: false,
      lastAutosavedData: {},
      hasFieldFocused: false
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.createUser = this.createUser.bind(this);
    this.autoSave = this.autoSave.bind(this);
    this.manualSave = this.manualSave.bind(this);
    this.onNextStep = this.onNextStep.bind(this);
    this.handleEmailBlur = this.handleEmailBlur.bind(this);
    this.handleFieldFocus = this.handleFieldFocus.bind(this);
    this.handleFieldBlur = this.handleFieldBlur.bind(this);
    this.updateSegments = this.updateSegments.bind(this);
  }

  componentDidMount() {
    const {
      fetchCategoriesIfNeeded, fetchBriefQuestions, subCategorySlug, categorySlug,
      setLazyRegistering, user, reset, showBanner, origin, fetchAttachments, fetchProjectIfNeeded,
      projectId, toggleAgencyQuestion
    } = this.props;

    /* make sure form is clean before starting PAP */
    reset();

    trackGoogle("pap", "started pap", origin);

    if (!user.logged_in) {
      trackGoogle("registration", "started registration", "lazy");
      trackMixpanel("Started registration", { "variant": "lazy" });
    }

    fetchCategoriesIfNeeded().then(res => {
      /* fetch project or sub-category brief questions depending on whether a
         project ID was provided */
      if (projectId) {
        fetchProjectIfNeeded({ projectId }).then(({ data }) => {
          const subCategory = getSubCategory(res, data.sub_category);
          const category = getCategory(res.data, data.category);

          trackGoogle("pap", "started brief", subCategory.slug);
          trackMixpanel("Started PAP Brief",{
            categorySlug: category.slug,
            subCategorySlug: subCategory.slug
          });
        });

        fetchAttachments(projectId);

      } else {
        const subCategory = getSubCategory(res, subCategorySlug);

        fetchBriefQuestions(subCategory.id);

        trackGoogle("pap", "started brief", subCategorySlug);
        trackMixpanel("Started PAP Brief",{
          categorySlug: categorySlug,
          subCategorySlug: subCategorySlug,
        });
      }
    });

    setLazyRegistering(!user.logged_in);

    toggleAgencyQuestion(!user.profile_data.is_agency_question_answered);

    /* show discount code banner if any */
    const code = getStoredDiscountCode(user.profile_data);
    const bannerContents = getBannerContents(code, 'apply');
    if (bannerContents) {
      showBanner(bannerContents.title, bannerContents.content, bannerContents.icon);
    }

    /* auto-save draft periodically */
    this.autoSaveTimer = setInterval(this.autoSave, 10000);
  }

  componentWillUnmount() {
    clearInterval(this.autoSaveTimer);
  }

  handleEmailBlur() {
    const { currentEmail, user, updateUser, setBannerError } = this.props;

    /* update user if already created */
    if (user.logged_in) {
      updateUser({
        id: user.profile_data.id,
        email: currentEmail
      }).catch((res) => {
        setBannerError('Invalid email', idx(res, _ => _.data.email[0]));
      });
    /* or try to create it otherwise */
    } else {
      this.createUser(currentEmail);
    }
  }

  autoSave() {
    /* submit form to save current form data */
    const { submit, user, fetchingQuestions } = this.props;
    const { hasFieldFocused } = this.state;

    if (user.logged_in && !hasFieldFocused && !fetchingQuestions) {
      this.setState({ autoSaving: true, manualSaving: false }, submit);
    }
  }

  manualSave() {
    /* submit form to save current form data */
    const { submit, user } = this.props;
    this.setState({ manualSaving: true, autoSaving: false }, submit);
  }

  onNextStep() {
    this.setState({ autoSaving: false, manualSaving: false }, this.props.submit);
  }

  validateQuestion(question, value, validationProps={}) {
    let validationError = '';
    const validationFuncs = getFieldPropsFromDefinition(question).fieldValidate || [];
    validationFuncs.forEach((validationFunc) => {
      validationError = validationFunc(value, {}, validationProps);
    });
    return validationError;
  }

  validateQuestions(data) {
    const { questions, setBannerError } = this.props;

    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      /* only brief questions... */
      if (question.name === 'create-account') {
        continue;
      }
      const value = data[question.name];
      const validationError = this.validateQuestion(
        question, value, { briefQuestions: questions, stepName: question.name }
      );
      if (validationError) {
        setBannerError(`Invalid answer to question '${question.label}'`, validationError);
        return validationError;
      }
    }
  }

  createUser(email) {
    const {
      createUserRequest, updateUserLocal, fetchDiscountCode, showBanner, setBannerError
    } = this.props;
    const userData = {
      is_client: true,
      email
    };

    return createUserRequest(userData).then((res) => {
      aliasWithMixpanel(res.data.id);
      identifyWithMixpanel(res.data.id);
      setMixpanelProfile(res.data.email, mixpanelProfileFromCsProfile(res.data), true);

      trackMixpanel("Registered", {
        "variant": "lazy",
        "userType": "client"
      });

      trackGoogle("registration", "registered", "client");

      updateUserLocal(res.data);

      /* retrieve pending discount code if any so that it's stored in the user object */
      const pendingCodeSlug = getPendingDiscountCode(true);
      if (pendingCodeSlug) {
        fetchDiscountCode(pendingCodeSlug).then(({ data }) => {
          /* show banner informing user of discount code */
          const bannerContents = getBannerContents(data, 'apply');
          showBanner(bannerContents.title, bannerContents.content, bannerContents.icon);
        });
      }
    }).catch((res) => {
      setBannerError('Invalid email', idx(res, _ => _.data.email[0]));
    });
  }

  updateSegments(data) {
    const { showingAgencyQuestion, setAgencySegment } = this.props;

    if (!showingAgencyQuestion || !data.is_agency) {
      return Promise.resolve();
    }
    return setAgencySegment(data.is_agency === 'yes');
  }

  handleSubmit(data) {
    const {
      createProjectRequest, updateAttachments, categories, subCategorySlug, setUnhandledError,
      projectId, fetchAttachments, attachments, updateProjectRequest, updateProjectLocal,
      categorySlug, setLazyRegistering, logout, toggleAgencyQuestion, setBannerError
    } = this.props;
    const { autoSaving, manualSaving, lastAutosavedData } = this.state;
    const briefAnswers = omit(data, ['file_upload', 'create-account', 'is_agency']);
    let promise = Promise.resolve();
    let creating = false;

    if (autoSaving || manualSaving) {
      /* don't auto-save if there's no brief question answered or there was no change in
         the form */
      if (
        autoSaving && (Object.keys(briefAnswers).length === 0 || isEqual(data, lastAutosavedData))
      ) {
        return;
      }

      this.setState({ lastAutosavedData: data });

    /* manually validate questions if submitting on user click, since we've disabled
       the field validation functions so that autosave could work */
    } else {
      if (this.validateQuestions(data)) {
        return;
      }
    }

    if (!projectId) {
       const subCategory = getSubCategory({ data: categories }, subCategorySlug);
       creating = true;
       promise = createProjectRequest({ sub_category: subCategory.id }).then((res) => {
         trackGoogle("pap", "created draft", subCategorySlug);
         trackFacebookCustomEvent("1726897664301628", "posted_draft");
         trackPerfectAudience("posted_draft");
         trackPerfectAudience(`posted_${categorySlug.replace(/-/g, '_')}_draft`);
         return res;
       });
    }

    /* marketing segments update can be done independently */
    this.updateSegments(data);

    return promise.then((res) => {
      const pId = creating? res.data.id : projectId;

      return updateAttachments(pId, attachments, data).then(() => {
        /* fetch updated list of attachments */
        fetchAttachments(pId);

        const projectData = {
          brief_answers: briefAnswers,
        };

        return updateProjectRequest(pId, projectData).then((res) => {
          updateProjectLocal(res.data);

          let nextUrl;

          /* if creating, change to brief URL with projectId */
          if (creating) {
            nextUrl = (
              getPapUrl({ projectId: pId, step: 'brief', subStep: 'all' }) +
              '?keep_scroll_on_url_change=true'
            );
          /* otherwise, go to PAP details section if not auto or manually saving */
          } else if (!(autoSaving || manualSaving)) {
            nextUrl = getPapUrl({ projectId: pId, step: 'details', subStep: 'package' });

            trackClickWithMixpanel(MP_CHANGED_PAP_SECTION_EVENT_NAME, {
              categorySlug: categorySlug,
              subCategorySlug: subCategorySlug,
              fromSectionName: "brief",
              toSectionName: "details",
              direction: "forward"
            });
            trackGoogle("pap", "started details", subCategorySlug);
            trackFacebookCustomEvent("1726897664301628", "started_details");
            trackMixpanel("Started PAP Details", {
              categorySlug: categorySlug,
              subCategorySlug: subCategorySlug,
            });

            setLazyRegistering(false);
            toggleAgencyQuestion(false);
          }

          if (nextUrl) {
            browserHistory.push(nextUrl)
          }
        });
      });
    }).catch((res) => {
      if (res.status === 403) {
        logout(undefined, true);
        return;
      }

      if (idx(res, _ => _.data.filename[0])) {
        setBannerError('Invalid attachment', idx(res, _ => _.data.filename[0]));
        return;
      }

      let error = 'Error occured while saving the project';
      setUnhandledError(
        error, { response: res, step: 'PAP brief (all)' }, 'Please refresh the page and try again.'
      );
    });
  }

  handleFieldFocus() {
    this.setState({ hasFieldFocused: true });
  }

  handleFieldBlur() {
    this.setState({ hasFieldFocused: false });
  }

  renderSteps() {
    const {
      questions, displayFullScreen, hideFullScreen, fullScreen, setLazyRegistering,
      user
    } = this.props;

    return questions.map((question, i) => {
      const fieldProps = getFieldPropsFromDefinition(question, {
        displayFullScreen,
        hideFullScreen,
        fullScreen,
        setLazyRegistering,
        nextStepLink: null,
        fieldValidate: undefined,
        loginForbidden: user.logged_in,
        onFocus: this.handleFieldFocus,
        onBlur: this.handleFieldBlur
      });
      if (question.name === 'create-account') {
        fieldProps.onBlur = () => {
          this.handleFieldBlur();
          this.handleEmailBlur();
        };
      }

      return <PAPQuestionBase key={i} {...fieldProps} />
    });
  }

  render() {
    const {
      questions, handleSubmit, fetchingQuestions, valid, submitting, categories, subCategorySlug,
      user
    } = this.props;
    const subCategory = getSubCategory({ data: categories }, subCategorySlug);

    return (
      <PAPBase
        hidePrice={true}
        loadingData={fetchingQuestions}
        sectionName="brief"
        extraClasses={["brief all" + (submitting? " submitting" : "")]}
        currentStepIndex={0}
        currentSectionStepCount={0}
        onPrevStep={() => {}}
        onNextStep={this.onNextStep}
        onSave={this.manualSave}
        saveLabel={submitting? 'Saving...' : undefined}
        saveEnabled={user.logged_in && !submitting}
        nextEnabled={user.logged_in && !submitting}
        prevEnabled={false}
        currentFeatures={[]}
        currentAwards={[]}
        currentTips={[]}
        billables={null}
        {...this.props}>
          <Form onSubmit={handleSubmit(this.handleSubmit)}>
            <div className="row intro">
              <div className="columns medium-14 medium-offset-1 large-10 large-offset-3">
                <h1>{ subCategory? subCategory.name : '' } brief</h1>
                <p>Answer important questions to help creatives understand what you need.</p>
              </div>
            </div>
            { this.renderSteps() }
          </Form>
      </PAPBase>
    );
  }
}



// WEBPACK FOOTER //
// ./pap_v2/components/brief_all.jsx